// Core
import React, {
  useCallback,
  useState,
} from 'react';

// Libraries
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';

// Plugins
import {
  isDakotaModel,
} from '../../../../../plugins/crusher';

// Media
/* @TODO: Apply the others cards logic in the future */
import IFMStatus from '../../../../../media/images/maintenance/ifm.avif';
// import ConeJBox from '../../../../../media/images/maintenance/cone.png';
import Plc from '../../../../../media/images/maintenance/plc.avif';
import ComboUnitImage from '../../../../../media/images/maintenance/ComboUnit/ComboUnit.avif';

// Components
import { MachineCard } from '../../../../../components/MachineCard/MachineCard';
import { ModalPanel, ModalSize } from '../../../../../layout/elements/ModalPanel';
import { Paragraph, ParagraphWeight } from '../../../../../layout/elements/Paragraph';
import {
  CheckboxLine,
  RouterLine,
  Thermometer,
} from '../../../../../layout/icons';

// Modules
import { ComboUnit, ComboUnitType } from './ComboUnit';
import { IFM, IFMType } from './IFM';
import { Setup } from './Setup';
import { SensorScaling } from './SensorScaling';
import { Setpoint, SetpointType } from './Setpoint';
import { PLC } from './PLC';

// View
import { MaintenanceProps, MaintenanceType } from './Maintenance.types';
import {
  CardContainer,
  CardContent,
  Container,
  DiagnosticsCardsContainer,
  DiagnosticsSection,
} from './Maintenance.style';
import { MaintenanceCard } from '../../../../../components/MaintenanceCard';

function Maintenance({
  className,
  onChangeView,
  onClose,
  unitData,
  edgeStatus,
  showView,
  testId,
}: MaintenanceProps): JSX.Element | null {
  // Dependencies
  const { t } = useTranslation();
  const { isXl, isXxl } = useBreakpoint();
  const showDiagnostics: boolean = isXl || isXxl;

  // Check if the model is Dakota
  const isDakota: boolean = isDakotaModel(unitData?.crusherModelPLC);

  /* ***********************************************************************************************
  *************************************** INITIAL STATE ********************************************
  *********************************************************************************************** */

  type State = {
    selectedComboUnitView: ComboUnitType;
    selectedSetPointView: SetpointType;
    selectedIFMView: IFMType;
  };

  const initialState: State = {
    selectedComboUnitView: ComboUnitType.sensors,
    selectedSetPointView: SetpointType.crusher,
    selectedIFMView: IFMType.ifm,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  /* Update selected set point view */
  const handleSetPoint = useCallback((item: MenuItemType): void => {
    const setPointView: SetpointType | undefined = Object.values(SetpointType).find(
      (value) => value === item.key,
    );

    setState((prevState: State) => ({
      ...prevState,
      selectedSetPointView: setPointView || SetpointType.crusher,
    }));
  }, []);

  /* Update selected ComboUnit view */
  const handleComboUnit = useCallback((item: MenuItemType): void => {
    const comboUnitView: ComboUnitType | undefined = Object.values(ComboUnitType).find(
      (value) => value === item.key,
    );

    setState((prevState: State) => ({
      ...prevState,
      selectedComboUnitView: comboUnitView || ComboUnitType.sensors,
    }));
  }, []);

  /* Update selected IFM view */
  const handleIFM = useCallback((item: MenuItemType): void => {
    const ifmView: IFMType | undefined = Object.values(IFMType).find(
      (value) => value === item.key,
    );

    setState((prevState: State) => ({
      ...prevState,
      selectedIFMView: ifmView || IFMType.ifm,
    }));
  }, []);

  /* Render content based on the current view */
  let content: JSX.Element | null = null;

  const titleMap: Record<MaintenanceType, string> = {
    [MaintenanceType.maintenance]: t('view.crusher.maintenance.title'),
    [MaintenanceType.setup]: t('common.setup'),
    [MaintenanceType.sensorScaling]: t('common.sensorScaling'),
    [MaintenanceType.setPoint]: t('common.setPoint'),
    [MaintenanceType.ifm]: 'IFM / ASI',
    [MaintenanceType.comboUnit]: t('view.crusher.maintenance.comboUnit.title'),
    [MaintenanceType.plc]: t('view.crusher.maintenance.plc'),
    [MaintenanceType.cone]: t('view.crusher.maintenance.cone'),
  };

  if (showView === MaintenanceType.maintenance) {
    content = (
      <Container className={className} data-testid={testId}>
        <Paragraph weight={ParagraphWeight.bold}>
          {t('view.crusher.maintenance.description')}
        </Paragraph>
        <CardContainer>
          <CardContent>
            <MachineCard
              description={t('common.setup')}
              icon={<CheckboxLine />}
              onClick={() => onChangeView(MaintenanceType.setup)}
            />
          </CardContent>
          <CardContent>
            <MachineCard
              description={t('common.setPoint')}
              icon={<RouterLine />}
              onClick={() => onChangeView(MaintenanceType.setPoint)}
            />
          </CardContent>
          <CardContent>
            <MachineCard
              description={t('common.sensorScaling')}
              icon={<Thermometer />}
              onClick={() => onChangeView(MaintenanceType.sensorScaling)}
            />
          </CardContent>
        </CardContainer>
        {showDiagnostics && (
          <DiagnosticsSection>
            <Paragraph weight={ParagraphWeight.bold}>
              {t('common.hmiDiagnostics')}
            </Paragraph>
            <DiagnosticsCardsContainer>
              {!isDakota && (
                <MaintenanceCard
                  className={className}
                  imgSrc={ComboUnitImage}
                  testId="comboUnit"
                  text={t('view.crusher.maintenance.comboUnit.comboUnitCard')}
                  onClick={() => onChangeView(MaintenanceType.comboUnit)}
                />
              )}
              <MaintenanceCard
                className={className}
                imgSrc={Plc}
                testId="plc"
                text={t('view.crusher.maintenance.plc')}
                onClick={() => onChangeView(MaintenanceType.plc)}
              />
              <MaintenanceCard
                className={className}
                imgSrc={IFMStatus}
                testId="ifm"
                text="IFM / ASI"
                onClick={() => onChangeView(MaintenanceType.ifm)}
              />
              {/* @TODO: Apply the others cards logic in the future
                <MaintenanceCard
                  className={className}
                  imgSrc={ConeJBox}
                  testId="ifm"
                  text="Cone & J-Box"
                  onClick={() => onChangeView(MaintenanceType.cone)}
                />
              */}
            </DiagnosticsCardsContainer>
          </DiagnosticsSection>
        )}
      </Container>
    );
  } else if (showView === MaintenanceType.setup) {
    content = (
      <Setup
        unitData={unitData}
        edgeStatus={edgeStatus}
      />
    );
  } else if (showView === MaintenanceType.sensorScaling) {
    content = (
      <SensorScaling
        unitData={unitData}
        testId="sensor-scaling"
      />
    );
  } else if (showView === MaintenanceType.setPoint) {
    content = (
      <Setpoint
        onChangeView={handleSetPoint}
        unitData={unitData}
        edgeStatus={edgeStatus}
        selectedView={state.selectedSetPointView}
        testId="setpoint"
      />
    );
  } else if (showView === MaintenanceType.ifm) {
    content = (
      <IFM
        onChangeView={handleIFM}
        selectedView={state.selectedIFMView}
        unitData={unitData}
        edgeStatus={edgeStatus}
      />
    );
  } else if (showView === MaintenanceType.comboUnit) {
    content = (
      <ComboUnit
        onChangeView={handleComboUnit}
        unitData={unitData}
        edgeStatus={edgeStatus}
        selectedView={state.selectedComboUnitView}
      />
    );
  } else if (showView === MaintenanceType.plc) {
    content = (
      <PLC
        unitData={unitData}
        edgeStatus={edgeStatus}
      />
    );
  }

  return (
    <ModalPanel
      onClose={onClose}
      showModal
      size={ModalSize.xl}
      title={titleMap[showView]}
      onBack={
        showView !== MaintenanceType.maintenance
          ? () => onChangeView(MaintenanceType.maintenance)
          : false
      }
    >
      {content}
    </ModalPanel>
  );
}

export { Maintenance };
